export const EMERGENCY_RESPONSE_PROCEDURE = {
  id: 'emergency-response-procedure',
  title: 'Emergency Response Procedure',
  version: 'v1.0-2026-05-17',
  effectiveDate: '17/05/2026',
  audience: 'internal',
  content: `EMERGENCY RESPONSE PROCEDURE — RESERVO

Status: DRAFT — pending solicitor review

1. PURPOSE

This procedure defines Reservo's response to emergencies arising during or in connection with bookings on the Reservo platform.

2. SCOPE

2.1 This procedure applies to:
  (a) safety emergencies during active trips (collision, medical incident, assault, threat);
  (b) safety emergencies at pickup or drop-off;
  (c) child-safety emergencies;
  (d) lost contact or off-route trips;
  (e) data or cyber emergencies (cross-referenced to the Data Breach Response Plan);
  (f) emergencies involving Driver fatigue, impairment, or medical event.

3. PRIORITY ORDER

3.1 In any emergency, the priority order is:
  (a) preserve human life;
  (b) prevent further harm;
  (c) preserve evidence;
  (d) notify Reservo Operations;
  (e) notify regulators where required.

4. TRIGGERS

4.1 An emergency response is triggered when ANY of the following occurs:
  (a) a Rider or Driver activates the in-app SOS button;
  (b) a Rider, Driver, or third party calls or emails Reservo reporting an emergency;
  (c) automated platform monitoring detects an off-route trip, prolonged stop in an unusual location, or unresponsive Driver location ping;
  (d) a Critical-severity incident report is submitted via the in-app form;
  (e) external authority (police, ambulance, fire) contacts Reservo about a trip;
  (f) a passenger fails to confirm receipt of trip completion.

5. SOS BUTTON HANDLING

5.1 When an SOS is activated:
  (a) the Reservo Operations team is alerted by SMS and email within 1 minute;
  (b) the trip's GPS location, Rider details, Driver details, and vehicle details are surfaced on the admin Operations dashboard;
  (c) Operations attempts contact with the activating user within 5 minutes via in-app call, SMS, and phone;
  (d) if the user confirms a false alarm, the SOS is closed with a logged reason;
  (e) if the user confirms an emergency or cannot be reached, Operations proceeds to escalation (see Section 6).

5.2 Reservo retains all SOS events in the incident register for 7 years.

6. ESCALATION

6.1 LEVEL 1 — Operations response (0 to 15 minutes from trigger):
  (a) attempt contact with affected user;
  (b) attempt contact with the other party on the trip;
  (c) assess the situation from available data;
  (d) record incident details in the register.

6.2 LEVEL 2 — Authority contact (15 to 30 minutes from trigger, sooner if life-threat clear):
  (a) call NSW Police on 000 if a crime, immediate violence, missing person, or life-threatening medical event is suspected;
  (b) call NSW Ambulance via 000 for serious medical events;
  (c) provide police or ambulance with: GPS location, vehicle make/model/registration, driver name, rider name, trip reference number, nature of the emergency;
  (d) maintain contact with the authority and the affected user until the emergency is resolved.

6.3 LEVEL 3 — Family / emergency-contact notification:
  (a) notify the affected user's nominated emergency contact where the situation warrants it;
  (b) coordinate with police if the matter is a missing-person or violent crime;
  (c) communicate factually, briefly, and without speculation.

6.4 LEVEL 4 — Regulatory notification:
  (a) notify the NSW Point to Point Transport Commissioner within statutory timeframes if the matter is a Notifiable Occurrence;
  (b) notify the Office of the Australian Information Commissioner if data has been compromised (see Data Breach Response Plan).

7. SPECIFIC EMERGENCY TYPES

7.1 Collision involving the Reservo vehicle:
  (a) Driver calls 000 first if anyone is injured;
  (b) Driver activates SOS;
  (c) Driver does not move injured persons unless further harm is imminent;
  (d) Driver exchanges details with other parties per NSW Road Rules;
  (e) Driver does not admit fault on scene;
  (f) Driver photographs the scene and damage;
  (g) Reservo Operations records the event as a Notifiable Occurrence and reports to the Commissioner within 24 hours.

7.2 Assault, sexual misconduct, or threat:
  (a) the affected party calls 000 first if in immediate danger;
  (b) the affected party activates SOS or contacts Reservo as soon as safe;
  (c) Reservo Operations preserves trip records, messages, and GPS;
  (d) Reservo Operations does not contact the alleged perpetrator until cleared with police;
  (e) Reservo cooperates fully with police investigation;
  (f) the matter is reported to the Commissioner.

7.3 Medical emergency in the vehicle:
  (a) Driver calls 000 and follows operator instructions;
  (b) Driver pulls over safely and stops;
  (c) Driver does not move the passenger unless instructed;
  (d) Driver activates SOS;
  (e) Reservo notifies the passenger's emergency contact if registered.

7.4 Child safety emergency:
  (a) follow Child Safety Policy clause 7 (unaccompanied minor) or clause 8 (reporting obligations);
  (b) involve NSW Police on 000 in immediate danger, NSW Child Protection Helpline 132 111 otherwise.

7.5 Cyber or data emergency:
  (a) follow the Data Breach Response Plan;
  (b) contain, assess, notify;
  (c) Operator (David Williams) is the incident lead.

8. POST-INCIDENT ACTIONS

8.1 Within 24 hours of the emergency resolution:
  (a) finalise incident log entries;
  (b) preserve evidence (GPS, messages, ratings, support tickets);
  (c) check on the wellbeing of affected persons;
  (d) commence internal review.

8.2 Within 7 days:
  (a) provide a written summary to affected persons on request;
  (b) provide regulatory notifications as required;
  (c) consider whether suspension or termination of any account is required.

8.3 Within 30 days:
  (a) complete root-cause analysis;
  (b) identify corrective actions;
  (c) update training, procedures, or platform controls as required;
  (d) close the incident in the register.

9. CONTACTS

9.1 Reservo Operations: admin@reservo.au, 0435 679 630 (24/7 in-app SOS).

9.2 Emergency services: 000.

9.3 NSW Police Assistance Line (non-urgent): 131 444.

9.4 NSW Child Protection Helpline: 132 111.

9.5 Lifeline (mental-health crisis): 13 11 14.

9.6 NSW Point to Point Transport Commissioner: pointtopoint.nsw.gov.au.

10. TRAINING

10.1 Drivers receive emergency-response training at onboarding covering: SOS use, collision response, medical event response, threat de-escalation, child safety, assistance-animal scenarios.

10.2 Refresher modules are delivered annually.

11. REVIEW

11.1 This procedure is reviewed annually and after any significant emergency or near-miss.

---
VERSION HISTORY
v1.0 — 17/05/2026 — Initial draft for solicitor review.

---
THIS DOCUMENT IS A DRAFT FOR PRE-LAUNCH REVIEW.`,
};